import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { signOut } from 'firebase/auth';
import { auth } from '../../api/firebase';
import { getFleet, getReportToday } from '../../api/gestor';
import { ToastProvider, Icon } from './ui';
import { KpiStrip, HealthGrid, DailyReportCard } from './OverviewTab';
import { QuadrasTab } from './QuadrasTab';
import { BillingTab } from './BillingTab';
import { ReportsTab } from './ReportsTab';
import { NewArenaModal } from './NewArenaModal';
import './gestor.css';

type Tab = 'overview' | 'quadras' | 'pagamentos' | 'relatorios';

function Clock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  return (
    <span className="nav-clock mono">
      {now.toLocaleDateString('pt-BR')} <b>{now.toLocaleTimeString('pt-BR')}</b>
    </span>
  );
}

const Gestor = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('overview');
  const [openSlug, setOpenSlug] = useState<string | null>(null);
  const [newArenaOpen, setNewArenaOpen] = useState(false);

  const { data: fleet, refetch: refetchFleet } = useQuery({
    queryKey: ['fleet'],
    queryFn: getFleet,
    refetchInterval: 30000,
  });
  const { data: report } = useQuery({
    queryKey: ['report-today'],
    queryFn: getReportToday,
    staleTime: 10 * 60 * 1000,
  });

  const openCourt = (slug: string) => {
    setTab('quadras');
    setOpenSlug(slug);
  };

  return (
    <ToastProvider>
      <div className="gestor-root">
        <nav className="nav">
          <div className="brand">
            <div className="brand-mark">A!</div>
            <div className="brand-text">
              <b>Apertai</b>
              <small>Painel do Gestor</small>
            </div>
          </div>
          <div className="tabs">
            <button className={`tab ${tab === 'overview' ? 'active' : ''}`} onClick={() => setTab('overview')}>
              <span className="tdot" /> Visão Geral
            </button>
            <button className={`tab ${tab === 'quadras' ? 'active' : ''}`} onClick={() => setTab('quadras')}>
              Quadras
            </button>
            <button className={`tab ${tab === 'pagamentos' ? 'active' : ''}`} onClick={() => setTab('pagamentos')}>
              Pagamentos
            </button>
            <button className={`tab ${tab === 'relatorios' ? 'active' : ''}`} onClick={() => setTab('relatorios')}>
              Relatórios
            </button>
          </div>
          <div className="nav-right">
            <Clock />
            <button className="nav-cta" onClick={async () => { await signOut(auth); navigate('/login'); }}>
              Sair
            </button>
          </div>
        </nav>

        {tab === 'overview' && (
          <section className="page active">
            <div className="page-head">
              <div>
                <h1>Visão Geral</h1>
                <div className="sub">
                  Estado de toda a operação em tempo real ·{' '}
                  <b>{fleet ? `${fleet.aggregates.total} quadras` : '…'}</b> conectadas
                </div>
              </div>
              <button className="btn btn-green" onClick={() => setNewArenaOpen(true)}>
                <Icon name="plus" size={14} /> Criar nova arena
              </button>
            </div>
            <NewArenaModal open={newArenaOpen} onClose={() => setNewArenaOpen(false)} />

            <KpiStrip fleet={fleet} report={report} />

            <div className="sec-head">
              <h2><span className="underline" /> Saúde das arenas</h2>
              <div className="right">
                <span className="pill"><span className="k">clique</span> para gerenciar</span>
              </div>
            </div>
            <HealthGrid fleet={fleet} onOpenCourt={openCourt} />

            <div className="sec-head">
              <h2><span className="underline" /> Relatório de erros do dia</h2>
            </div>
            <DailyReportCard onOpenCourt={openCourt} />
          </section>
        )}

        {tab === 'quadras' && (
          <section className="page active">
            <div className="page-head">
              <div>
                <h1>Quadras</h1>
                <div className="sub">
                  Clique em uma quadra para services, câmeras, métricas, patrocínios e ações
                </div>
              </div>
              <button className="btn btn-ghost btn-sm" onClick={() => refetchFleet()}>
                <Icon name="reset" size={14} /> Atualizar
              </button>
            </div>
            <QuadrasTab fleet={fleet} openSlug={openSlug} setOpenSlug={setOpenSlug}
              refetchFleet={() => refetchFleet()} />
          </section>
        )}

        {tab === 'pagamentos' && (
          <section className="page active">
            <BillingTab />
          </section>
        )}

        {tab === 'relatorios' && (
          <section className="page active">
            <ReportsTab />
          </section>
        )}
      </div>
    </ToastProvider>
  );
};

export default Gestor;
